/**
 * Public Landing Page — Leynix Pilot
 * 
 * PILOT MODE:
 * Simple, clean entry point for A Touch of Angels pilot users.
 * Three clear paths:
 * 1. Organization Workspace — For Cat, Joanna, Jayke (office staff)
 * 2. My Leynix — For caregivers
 * 3. Apply Now — For job applicants
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Users, Briefcase, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [showContent, setShowContent] = useState(false);
  const [logoReady, setLogoReady] = useState(false);

  useEffect(() => {
    axios.get(`${API}/agencies/by-name/A Touch of Angels Home Care Agency/public`)
      .then((r) => setAgency(r.data))
      .catch(() => setAgency(null));
  }, []);

  // Logo animation sequence
  useEffect(() => {
    // Show logo immediately
    const logoTimer = setTimeout(() => setLogoReady(true), 100);
    // Show content after logo has time to display (2.5 seconds for nice branding moment)
    const contentTimer = setTimeout(() => setShowContent(true), 2500);
    
    return () => {
      clearTimeout(logoTimer);
      clearTimeout(contentTimer);
    };
  }, []);

  // If user is already logged in, redirect to their workspace
  useEffect(() => {
    if (!loading && user) {
      if (user.role === 'employee' || user.role === 'caregiver') {
        navigate('/my-leynix');
      } else {
        navigate('/home');
      }
    }
  }, [user, loading, navigate]);

  // Show nothing while auth is loading
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D1B33] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#176DFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1B33] flex flex-col">
      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Logo & Branding - Appears First, Centered */}
        <div className={`flex flex-col items-center transition-all duration-700 ${
          logoReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        } ${!showContent ? 'flex-1 justify-center' : ''}`}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 md:w-20 md:h-20 relative">
              {/* Hexagon with clock icon */}
              <svg viewBox="0 0 60 60" className="w-full h-full">
                <path 
                  d="M30 5 L52 17.5 L52 42.5 L30 55 L8 42.5 L8 17.5 Z" 
                  fill="none" 
                  stroke="#176DFF" 
                  strokeWidth="2"
                  style={{
                    strokeDasharray: 200,
                    strokeDashoffset: logoReady ? 0 : 200,
                    transition: 'stroke-dashoffset 1s ease-out'
                  }}
                />
                <circle 
                  cx="30" cy="30" r="12" 
                  fill="none" 
                  stroke="#176DFF" 
                  strokeWidth="2"
                  style={{
                    opacity: logoReady ? 1 : 0,
                    transition: 'opacity 0.5s ease-out 0.5s'
                  }}
                />
                <line 
                  x1="30" y1="30" x2="30" y2="22" 
                  stroke="#176DFF" 
                  strokeWidth="2" 
                  strokeLinecap="round"
                  style={{
                    opacity: logoReady ? 1 : 0,
                    transition: 'opacity 0.3s ease-out 0.8s'
                  }}
                />
                <line 
                  x1="30" y1="30" x2="36" y2="30" 
                  stroke="#176DFF" 
                  strokeWidth="2" 
                  strokeLinecap="round"
                  style={{
                    opacity: logoReady ? 1 : 0,
                    transition: 'opacity 0.3s ease-out 0.9s'
                  }}
                />
              </svg>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white tracking-tight">
              Leyni<span className="text-[#176DFF]">x</span>
            </h1>
          </div>

          {/* Tagline */}
          <p className={`text-lg md:text-xl text-[#A0AEC0] text-center max-w-lg transition-all duration-500 delay-300 ${
            logoReady ? 'opacity-100' : 'opacity-0'
          } ${showContent ? 'mb-8 md:mb-12' : 'mb-0'}`}>
            Run Your Organization from One Connected Platform.
          </p>
        </div>

        {/* Entry Points - Hidden Initially, Appears After Splash */}
        {showContent && (
          <div className="w-full max-w-3xl animate-fade-in-up">
            <div className="grid md:grid-cols-3 gap-4">
              {/* Organization Workspace */}
              <Link 
                to="/login" 
                className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#176DFF]/50 rounded-xl p-6 transition-all duration-200"
                data-testid="org-workspace-entry"
              >
                <div className="w-12 h-12 bg-[#0D1B33] border border-white/20 rounded-lg flex items-center justify-center mb-4 group-hover:border-[#176DFF]/50 transition-colors">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">Organization Workspace</h3>
                <p className="text-sm text-[#A0AEC0] mb-4">For executives, HR, operations, and office staff.</p>
                <div className="flex items-center text-[#176DFF] text-sm font-medium">
                  Sign In <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* My Leynix */}
              <Link 
                to="/login?portal=caregiver" 
                className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#176DFF]/50 rounded-xl p-6 transition-all duration-200"
                data-testid="my-leynix-entry"
              >
                <div className="w-12 h-12 bg-[#176DFF]/20 border border-[#176DFF]/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#176DFF]/30 transition-colors">
                  <Users className="w-6 h-6 text-[#176DFF]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">My Leynix</h3>
                <p className="text-sm text-[#A0AEC0] mb-4">For caregivers, employees, and field staff.</p>
                <div className="flex items-center text-[#176DFF] text-sm font-medium">
                  Sign In <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Apply Now */}
              <Link 
                to={agency?.id ? `/apply/${agency.id}` : "/track"} 
                className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D4C19C]/50 rounded-xl p-6 transition-all duration-200"
                data-testid="apply-now-entry"
              >
                <div className="w-12 h-12 bg-[#D4C19C]/20 border border-[#D4C19C]/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D4C19C]/30 transition-colors">
                  <Briefcase className="w-6 h-6 text-[#D4C19C]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">Apply Now</h3>
                <p className="text-sm text-[#A0AEC0] mb-4">Looking for a caregiving position? Start here.</p>
                <div className="flex items-center text-[#D4C19C] text-sm font-medium">
                  Apply <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>

            {/* Track Application Link */}
            <div className="mt-6 text-center">
              <Link 
                to="/track" 
                className="text-sm text-[#A0AEC0] hover:text-white transition-colors"
                data-testid="track-application-link"
              >
                Already applied? Track your application →
              </Link>
            </div>
          </div>
        )}
      </main>

      {/* Footer - Only shows after cards appear */}
      {showContent && (
        <footer className="py-6 px-6 text-center animate-fade-in">
          <p className="text-xs text-[#64748B]">
            AI Assists. People Decide.
          </p>
          <p className="text-xs text-[#475569] mt-1">
            © {new Date().getFullYear()} Leynix · Developed and owned by Anessa Leydig
          </p>
        </footer>
      )}
      
      {/* CSS for animations */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
